import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2SitemapStore, findPageDescriptorById, findNavigationItems, findActiveRootPage, hasMegaMenu } from '@pm2/sitemap-utils';
import { pm2InterfaceStore } from '@pm2/store';
import { useLocation } from '@provider/navigation';
import { breakpoints } from '@ui/css';
import { printHtmlToJsx, simpleHtmlParsers } from '@utils/ast';
import { throttleToAnimationFrame } from '@utils/common';
import { useScrollLocking, useLoaderState, useClickOutsideListener, ShowFor, Burger, CSSRefTransition, Chevron, SizeTransition } from '@utils/react';
import { useOpenMegaMenu } from '../MegaMenu/hook.open-mega-menu.js';
import { List } from './List.js';
import { ENTER_EXIT_TRANSITION_DURATION_MS, wrapperCls } from './List.style.js';
import { mobileNavigationTokens, navigationCls, headerCls, burgerCls, headingButtonWrapper, headingButtonFadeInDurationMs, headingButtonCls, headingCls, stateWrapperCls, throbberCls, throbberWrapperCls, chevronCls, backdropCls } from './Navigation.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function MobileNavigation({
  toolsRef
}) {
  const openMegaMenu = useOpenMegaMenu("default");
  const location = useLocation();
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const megaMenu = pm2InterfaceStore.useSelector(state => state.megaMenu)?.map(id => findPageDescriptorById(sitemap, id)).filter(it => it !== undefined);
  const items = findNavigationItems(sitemap);
  const activeRootPage = megaMenu?.[0] ?? findActiveRootPage(sitemap, location.pathname);
  const toggleNavigation = () => {
    setExpanded(!expanded);
  };
  const collpaseNavigation = () => {
    setExpanded(false);
  };
  const navRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  useScrollLocking(expanded);
  const toolsWidth = useToolswidth(toolsRef);
  const hasExpandedMegaMenu = megaMenu?.[0] === activeRootPage;
  const [isLoadingMegaMenu, setLoadingMegaMenu] = useState(false);
  const showLoader = useLoaderState(isLoadingMegaMenu);

  // We capture clicks during the capture phase in order to allow onClickOutside
  // since we will want to close the navigation no matter what is clicked (even
  // if it is buttons that trigger a new useClickOutside), since the mobile nav
  // shouldn't exist when anything else is open.
  useClickOutsideListener(collpaseNavigation, navRef, {
    useCapturePhase: true,
    disabled: hasExpandedMegaMenu
  });
  const onButtonClick = () => {
    if (hasMegaMenu(activeRootPage)) {
      onMegaMenuOpen();
    } else {
      toggleNavigation();
    }
  };
  const onMegaMenuOpen = async () => {
    if (!activeRootPage) {
      return;
    }
    collpaseNavigation();
    setLoadingMegaMenu(true);
    try {
      await openMegaMenu(activeRootPage, {
        isMobile: true
      });
      setLoadingMegaMenu(false);
    } catch {
      setLoadingMegaMenu(false);
    }
  };
  const activeRootTitle = activeRootPage?.path === "/" ? "Forside" : activeRootPage?.title;
  return /*#__PURE__*/jsx("nav", {
    ref: navRef,
    className: navigationCls,
    style: mobileNavigationTokens.__override({
      toolsWidth: `${toolsWidth}px`
    }),
    children: /*#__PURE__*/jsxs(ShowFor, {
      breakpoint: breakpoints.mobile,
      children: [/*#__PURE__*/jsxs("header", {
        className: headerCls,
        children: [/*#__PURE__*/jsx(ShowFor, {
          className: burgerCls,
          breakpoint: breakpoints.mobile,
          children: /*#__PURE__*/jsx(Burger, {
            size: 16,
            "aria-label": "Åbn navigation",
            toggled: expanded,
            onClick: toggleNavigation
          })
        }), /*#__PURE__*/jsx(TransitionGroup, {
          component: "div",
          className: headingButtonWrapper,
          children: !!activeRootTitle && /*#__PURE__*/jsx(CSSRefTransition, {
            classNames: headingButtonCls,
            timeout: headingButtonFadeInDurationMs,
            children: ref => /*#__PURE__*/jsxs("button", {
              ref: ref,
              onClick: onButtonClick,
              className: cx(headingButtonCls, hasExpandedMegaMenu && "expandedMegaMenu"),
              children: [/*#__PURE__*/jsx("h3", {
                className: headingCls,
                children: printHtmlToJsx(activeRootTitle ?? "", simpleHtmlParsers)
              }), hasMegaMenu(activeRootPage) && /*#__PURE__*/jsxs("span", {
                className: stateWrapperCls,
                children: [/*#__PURE__*/jsx("span", {
                  className: cx(throbberWrapperCls, !showLoader.current && "hidden"),
                  children: /*#__PURE__*/jsx("span", {
                    className: throbberCls
                  })
                }), /*#__PURE__*/jsx("span", {
                  className: cx(chevronCls, showLoader.current && "hidden"),
                  children: /*#__PURE__*/jsx(Chevron, {
                    vertical: true,
                    inversed: hasExpandedMegaMenu,
                    size: 6,
                    lineWidth: 1.5
                  })
                })]
              })]
            })
          }, activeRootTitle)
        })]
      }), /*#__PURE__*/jsx(SizeTransition, {
        axis: "vertical",
        in: expanded,
        classNames: wrapperCls,
        timeout: ENTER_EXIT_TRANSITION_DURATION_MS,
        mountOnEnter: true,
        unmountOnExit: true,
        children: ref => /*#__PURE__*/jsx(List, {
          items: items,
          activeRootPage: activeRootPage,
          onLinkClick: collpaseNavigation,
          listRef: ref
        })
      }), /*#__PURE__*/jsx("div", {
        role: "none",
        className: cx(backdropCls, expanded && "visible"),
        onClick: collpaseNavigation
      })]
    })
  });
}

/**
 * We need to know the size of the tools in order to ensure that navigation and
 * tools cannot overflow into each other
 */
function useToolswidth(toolsRef) {
  const [width, setWidth] = useState(getToolsWidth(toolsRef));
  useEffect(() => {
    const updateWidth = () => {
      if (!toolsRef.current) {
        setTimeout(updateWidth, 50);
      }
      setWidth(getToolsWidth(toolsRef));
    };
    updateWidth();
    const throttled = throttleToAnimationFrame(updateWidth);
    window.addEventListener("resize", throttled);
    window.addEventListener("orientationchange", throttled);
    return () => {
      window.removeEventListener("resize", throttled);
      window.removeEventListener("orientationchange", throttled);
    };
  }, [toolsRef]);
  return width;
}
function getToolsWidth(toolsRef) {
  const tools = toolsRef?.current;

  // In case the tools aren't available at the moment, assume they don't fill
  // anything
  if (!tools) {
    return 0;
  }
  return tools.clientWidth;
}

export { MobileNavigation };
//# sourceMappingURL=Navigation.js.map
